<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { io } from 'socket.io-client'
import {
  Chart,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js'
import { WS_URL } from '../config.js'
import { PALETTE } from '../config/palette.js'
import { fetchEventInfo, fetchSplits } from '../utils/checkpoint-data.js'
import {
  FINISH_TYPES,
  START_TYPES,
  markerFullLabel,
  markerType,
  passTimesForMarker
} from '../utils/checkpoint-results.js'
import {
  BUCKET_OPTIONS,
  FINISH_KEY,
  buildArrivalBuckets,
  finishPassTimes,
  formatDay,
  formatHM,
  suggestBucketMinutes
} from '../utils/arrival-flow.js'
import { formatClock, parsePunchTime } from '../utils/polish-time.js'
import { isSupportRole } from '../utils/runner-role.js'

Chart.register(BarElement, BarController, CategoryScale, LinearScale, Tooltip, Legend)

// Przepływ zawodników na punkcie albo mecie: liczba wejść w przedziałach 15/30/60 min, kolory = kategorie.
// Na żywo: odbicia RFID od razu z websocketu (`new_punch`), reszta (GPS, SportIdent, ręczne) z /splits co 15 s.
const REFRESH_MS = 15000
const CLOCK_MS = 30000

const route = useRoute()
const eventAlias = route.params.eventAlias
const markerParam = String(route.params.markerId)
const isFinish = markerParam === FINISH_KEY

const data = ref(null)
const eventInfo = ref(null)
const error = ref('')
const connected = ref(false)
const bucketMinutes = ref(null) // null = dobierz automatycznie po pierwszym wczytaniu
const showTable = ref(false)
const now = ref(Date.now())
const livePasses = ref(new Map())
const canvasRef = ref(null)
let chart = null
let socket = null
let hasConnectedOnce = false
let refreshTimer = null
let clockTimer = null

const marker = computed(() => {
  if (!data.value) return null
  if (isFinish) return data.value.markers.find(m => FINISH_TYPES.includes(markerType(m))) || null
  return data.value.markers.find(m => String(m.id) === markerParam) || null
})

const title = computed(() => {
  if (isFinish) return 'Meta'
  return marker.value ? markerFullLabel(marker.value) : `Punkt ${markerParam}`
})

const passTimes = computed(() => {
  if (!data.value) return new Map()
  const passes = isFinish
    ? finishPassTimes(data.value.runners, data.value.markers, data.value.splits)
    : passTimesForMarker(data.value.splits, markerParam)
  for (const [runnerId, ms] of livePasses.value) {
    const prev = passes.get(runnerId)
    if (prev == null || ms < prev) passes.set(runnerId, ms)
  }
  return passes
})

// W trakcie wydarzenia oś sięga do „teraz”, żeby było widać, że na punkcie zrobiło się pusto.
const isLive = computed(() => {
  const info = eventInfo.value
  if (!info || info.startMs == null) return false
  return now.value >= info.startMs && (info.endMs == null || now.value <= info.endMs)
})

const effectiveBucket = computed(() => bucketMinutes.value ?? suggestBucketMinutes(passTimes.value))

const flow = computed(() => {
  if (!data.value) return null
  return buildArrivalBuckets({
    runners: data.value.runners,
    passTimes: passTimes.value,
    categories: data.value.categories,
    bucketMinutes: effectiveBucket.value,
    untilMs: isLive.value ? now.value : null
  })
})

const startersCount = computed(() =>
  (data.value?.runners || []).filter(r => !Number(r.abs) && !isSupportRole(r)).length
)

const lastPassMs = computed(() => {
  let last = null
  for (const ms of passTimes.value.values()) if (last == null || ms > last) last = ms
  return last
})

const currentBucket = computed(() => {
  const f = flow.value
  if (!f || !isLive.value || f.buckets.length === 0) return null
  const last = f.buckets[f.buckets.length - 1]
  return now.value >= last.startMs && now.value < last.endMs ? last : null
})

const multiDay = computed(() => {
  const b = flow.value?.buckets || []
  return b.length > 0 && formatDay(b[0].startMs) !== formatDay(b[b.length - 1].startMs)
})

function bucketLabel (bucket, index, buckets) {
  const hm = formatHM(bucket.startMs)
  if (!multiDay.value) return hm
  const day = formatDay(bucket.startMs)
  return index === 0 || day !== formatDay(buckets[index - 1].startMs) ? `${day} ${hm}` : hm
}

function rangeLabel (bucket) {
  return `${formatHM(bucket.startMs)}–${formatHM(bucket.endMs)}`
}

function chartConfig () {
  const f = flow.value
  return {
    labels: f.buckets.map((b, i, all) => bucketLabel(b, i, all)),
    datasets: f.categories.map(cat => ({
      label: cat.name,
      data: f.buckets.map(b => b.counts[cat.id] || 0),
      backgroundColor: cat.color,
      borderColor: PALETTE.surface,
      borderWidth: { top: 1 },
      borderSkipped: 'bottom',
      categoryPercentage: 0.9,
      barPercentage: 1
    }))
  }
}

function renderChart () {
  if (!canvasRef.value || !flow.value || flow.value.buckets.length === 0) {
    if (chart) { chart.destroy(); chart = null }
    return
  }
  const config = chartConfig()
  // Canvas bywa tworzony od nowa (przełączenie Wykres/Tabela, pierwsze wejście) — wtedy nowy wykres.
  if (chart && chart.canvas !== canvasRef.value) { chart.destroy(); chart = null }
  if (chart) {
    chart.data.labels = config.labels
    chart.data.datasets = config.datasets
    chart.update('none')
    return
  }
  chart = new Chart(canvasRef.value, {
    type: 'bar',
    data: config,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { color: PALETTE.textSecondary, autoSkip: true, maxRotation: 0 }
        },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { color: PALETTE.textSecondary, precision: 0 },
          grid: { color: PALETTE.borderSoft },
          title: { display: true, text: 'Wejścia', color: PALETTE.textSecondary }
        }
      },
      plugins: {
        legend: {
          position: 'top',
          labels: { color: PALETTE.text, boxWidth: 12, boxHeight: 12 }
        },
        tooltip: {
          filter: item => item.parsed.y > 0,
          callbacks: {
            title: items => {
              const bucket = flow.value?.buckets[items[0]?.dataIndex]
              return bucket ? rangeLabel(bucket) : ''
            },
            label: item => ` ${item.dataset.label}: ${item.parsed.y}`,
            footer: items => {
              const bucket = flow.value?.buckets[items[0]?.dataIndex]
              return bucket ? `Razem: ${bucket.total}` : ''
            }
          }
        }
      }
    }
  })
}

watch(flow, renderChart, { flush: 'post' })
watch(showTable, renderChart, { flush: 'post' })

async function load () {
  try {
    data.value = await fetchSplits(eventAlias)
    error.value = ''
  } catch (e) {
    error.value = 'Nie udało się odświeżyć danych — próbuję dalej.'
  }
}

// Odbicie RFID: punkt po marker_id; meta/start bywają przypisane bez punktu (mode finish/start).
function punchMatches (punch) {
  if (isFinish) {
    if (punch.mode === 'finish') return true
    const m = punch.marker_id != null && data.value?.markers.find(x => String(x.id) === String(punch.marker_id))
    return !!m && FINISH_TYPES.includes(markerType(m))
  }
  if (punch.marker_id != null) return String(punch.marker_id) === markerParam
  if (!marker.value) return false
  const type = markerType(marker.value)
  return (punch.mode === 'finish' && FINISH_TYPES.includes(type)) ||
    (punch.mode === 'start' && START_TYPES.includes(type))
}

function handlePunch (punch) {
  if (!punch || punch.runner_id == null || !punchMatches(punch)) return
  const ms = parsePunchTime(punch.punch_timestamp || punch.timestamp)
  if (ms == null) return
  const key = String(punch.runner_id)
  const prev = passTimes.value.get(key)
  if (prev != null && prev <= ms) return // liczy się pierwsze wejście
  livePasses.value = new Map(livePasses.value).set(key, ms)
}

function connectSocket (eventId) {
  socket = io(WS_URL)
  socket.on('connect', () => {
    connected.value = true
    socket.emit('join_event', eventId)
    // Odbicia z czasu przerwy nie przychodzą przez websocket — dociągamy /splits.
    if (hasConnectedOnce) load()
    hasConnectedOnce = true
  })
  socket.on('disconnect', (reason) => {
    connected.value = false
    if (reason === 'io server disconnect' && socket) socket.connect()
  })
  socket.on('connect_error', (err) => console.error('Websocket connect_error:', err.message))
  socket.on('new_punch', handlePunch)
}

function setBucket (minutes) {
  bucketMinutes.value = minutes
}

onMounted(() => {
  fetchEventInfo(eventAlias)
    .then(info => {
      eventInfo.value = info
      if (info.id != null) connectSocket(info.id)
    })
    .catch(() => { error.value = 'Nie udało się pobrać wydarzenia.' })
  load()
  refreshTimer = setInterval(load, REFRESH_MS)
  clockTimer = setInterval(() => { now.value = Date.now() }, CLOCK_MS)
})

onUnmounted(() => {
  clearInterval(refreshTimer)
  clearInterval(clockTimer)
  if (chart) { chart.destroy(); chart = null }
  if (socket) { socket.disconnect(); socket = null }
})
</script>

<template>
  <div class="arrival-chart-view">
    <router-link :to="{ name: 'Checkpoints', params: { eventAlias } }" class="small">← Wszystkie punkty</router-link>
    <h1 class="mt-1 mb-1">{{ title }} — przepływ zawodników</h1>
    <p v-if="eventInfo" class="text-muted mb-2">{{ eventInfo.name }}</p>

    <div class="toolbar mb-3">
      <span :class="['badge', connected ? 'bg-success' : 'bg-danger']">
        {{ connected ? 'Na żywo' : 'Brak połączenia' }}
      </span>
      <div class="btn-group btn-group-sm" role="group" aria-label="Przedział">
        <button
          v-for="minutes in BUCKET_OPTIONS"
          :key="minutes"
          type="button"
          :class="['btn', effectiveBucket === minutes ? 'btn-primary' : 'btn-outline-primary']"
          @click="setBucket(minutes)"
        >{{ minutes }} min</button>
      </div>
      <div class="btn-group btn-group-sm ms-auto" role="group" aria-label="Widok">
        <button type="button" :class="['btn', !showTable ? 'btn-primary' : 'btn-outline-primary']" @click="showTable = false">Wykres</button>
        <button type="button" :class="['btn', showTable ? 'btn-primary' : 'btn-outline-primary']" @click="showTable = true">Tabela</button>
      </div>
    </div>

    <div v-if="error" class="alert alert-warning py-1 small">{{ error }}</div>
    <div v-if="!data" class="text-muted">Wczytywanie…</div>
    <div v-else-if="!isFinish && !marker" class="alert alert-warning">Nie ma takiego punktu z międzyczasami w tym wydarzeniu.</div>
    <div v-else-if="!flow || flow.total === 0" class="text-muted">Nikt jeszcze nie dotarł {{ isFinish ? 'na metę' : 'na ten punkt' }}.</div>

    <template v-else>
      <div class="stats mb-3">
        <div class="stat">
          <span class="stat-label">Dotarło</span>
          <span class="stat-value">{{ flow.total }}<small> / {{ startersCount }}</small></span>
        </div>
        <div v-if="isLive" class="stat">
          <span class="stat-label">Teraz ({{ currentBucket ? rangeLabel(currentBucket) : `${effectiveBucket} min` }})</span>
          <span class="stat-value">{{ currentBucket ? currentBucket.total : 0 }}</span>
        </div>
        <div v-if="flow.peak" class="stat">
          <span class="stat-label">Szczyt</span>
          <span class="stat-value">{{ flow.peak.total }}<small> · {{ rangeLabel(flow.peak) }}</small></span>
        </div>
        <div class="stat">
          <span class="stat-label">Ostatnie wejście</span>
          <span class="stat-value">{{ formatClock(lastPassMs) }}</span>
        </div>
      </div>

      <div v-if="!showTable" class="chart-wrap">
        <canvas ref="canvasRef" role="img" :aria-label="`Liczba wejść na ${title} w przedziałach ${effectiveBucket} min, z podziałem na kategorie`"></canvas>
      </div>

      <div v-else class="table-responsive">
        <table class="table table-sm table-striped align-middle">
          <thead>
            <tr>
              <th>Przedział</th>
              <th v-for="cat in flow.categories" :key="cat.id" class="text-end">
                <span class="swatch" :style="{ backgroundColor: cat.color }"></span>{{ cat.name }}
              </th>
              <th class="text-end">Razem</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="bucket in flow.buckets" :key="bucket.startMs">
              <td class="text-nowrap">{{ multiDay ? `${formatDay(bucket.startMs)} ` : '' }}{{ rangeLabel(bucket) }}</td>
              <td v-for="cat in flow.categories" :key="cat.id" class="text-end">{{ bucket.counts[cat.id] || '' }}</td>
              <td class="text-end fw-bold">{{ bucket.total || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<style scoped>
.arrival-chart-view {
  width: 100%;
  text-align: left;
}

@media (min-width: 992px) {
  .arrival-chart-view {
    max-width: 1200px;
    margin: 0 auto;
  }
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.stat {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--sledz-border-soft);
  border-radius: 0.5rem;
  background: var(--sledz-surface);
  min-width: 8rem;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--sledz-text-muted);
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--sledz-text);
  font-variant-numeric: tabular-nums;
}

.stat-value small {
  font-size: 0.9rem;
  font-weight: 400;
  color: var(--sledz-text-secondary);
}

.chart-wrap {
  position: relative;
  height: min(65vh, 560px);
  min-height: 300px;
}

.swatch {
  display: inline-block;
  width: 0.7rem;
  height: 0.7rem;
  border-radius: 2px;
  margin-right: 0.35rem;
  vertical-align: baseline;
}
</style>
